<template>
  <v-dialog v-model="value" width="550" persistent>
    <v-card class="w-100 rounded-lg">
      <v-card-title>{{ $t('spedilink.links.manageListAssignment') }}</v-card-title>
      <v-card-text>
        <v-text-field
          v-model="searchQuery"
          :placeholder="$t('spedilink.links.searchLists')"
          outlined
          dense
          clearable
          prepend-inner-icon="mdi-magnify"
          class="mb-2"
          hide-details
        />
        <div v-if="listsLoading" class="d-flex justify-center py-6">
          <v-progress-circular indeterminate color="primary" />
        </div>
        <v-list v-else dense class="overflow-y-auto" style="max-height: 320px;">
          <v-list-item
            v-for="list in filteredLists"
            :key="list.id"
            ripple
            @click="toggleList(list.id)"
          >
            <v-list-item-action class="my-0 mr-2">
              <v-checkbox
                :input-value="isSelected(list.id)"
                color="primary"
                readonly
                @click.stop="toggleList(list.id)"
              />
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>{{ list.name }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item v-if="filteredLists.length === 0">
            <v-list-item-content>
              <v-list-item-title class="text-center lightText--text">
                {{ $t('spedilink.links.noListsFound') }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-card-text>
      <v-card-actions class="d-flex justify-end px-4 pb-4">
        <v-btn outlined :disabled="loading" @click="close()">
          {{ $t('common.cancel') }}
        </v-btn>
        <v-btn
          color="primary"
          class="ml-2"
          :loading="loading"
          :disabled="loading || !hasChanges"
          depressed
          @click="save()"
        >
          {{ $t('common.save') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Service from '@/services'
import { GUID_EMPTY } from '@/views/Spedilink/spedilink-constants'

export default {
  name: 'AddOfferToAnotherListDialog',
  props: {
    value: {
      type: Boolean,
      required: true
    },
    orderId: {
      type: String,
      default: null
    },
    type: {
      type: Number,
      required: true
    },
    currentLinkId: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      loading: false,
      selectedListIds: [],
      initialSelectedIds: [],
      lists: [],
      listsLoading: false,
      searchQuery: ''
    }
  },
  computed: {
    filteredLists () {
      if (!this.searchQuery) return this.lists
      const q = this.searchQuery.toLowerCase()
      return this.lists.filter(l => l.name.toLowerCase().includes(q))
    },
    hasChanges () {
      const selected = new Set(this.selectedListIds)
      const initial = new Set(this.initialSelectedIds)
      if (selected.size !== initial.size) return true
      for (const id of selected) {
        if (!initial.has(id)) return true
      }
      return false
    }
  },
  watch: {
    value: {
      immediate: true,
      handler (newVal) {
        if (newVal) {
          this.loadLists()
        } else {
          this.resetForm()
        }
      }
    }
  },
  methods: {
    isSelected (id) {
      return this.selectedListIds.includes(id)
    },
    toggleList (id) {
      const idx = this.selectedListIds.indexOf(id)
      if (idx === -1) {
        this.selectedListIds = [...this.selectedListIds, id]
      } else {
        this.selectedListIds = this.selectedListIds.filter(x => x !== id)
      }
    },
    async loadLists () {
      this.listsLoading = true
      try {
        const params = { type: this.type, page: 1, pageSize: 100 }
        if (this.orderId) params.markOrderId = this.orderId
        const { data } = await Service.get('spedilink/SpediLinks/GetList', params)
        this.lists = (data.results || [])
          .filter(l => l.id !== GUID_EMPTY && l.id !== this.currentLinkId)
        const preSelected = this.lists
          .filter(l => l.isAssigned)
          .map(l => l.id)
        this.selectedListIds = [...preSelected]
        this.initialSelectedIds = [...preSelected]
      } catch (e) {
      } finally {
        this.listsLoading = false
      }
    },
    close () {
      this.$emit('close')
      this.resetForm()
    },
    resetForm () {
      this.selectedListIds = []
      this.initialSelectedIds = []
      this.lists = []
      this.searchQuery = ''
    },
    async save () {
      this.loading = true
      try {
        const initialSet = new Set(this.initialSelectedIds)
        const selectedSet = new Set(this.selectedListIds)

        const linkIdsToAssign = this.selectedListIds.filter(id => !initialSet.has(id))
        const linkIdsToUnassign = this.initialSelectedIds.filter(id => !selectedSet.has(id))

        await Service.post('spedilink/SpediLinks/ReassignOrder', {
          orderId: this.orderId,
          linkIdsToAssign,
          linkIdsToUnassign
        })

        this.$emit('offer-added-to-list')
        this.close()
      } catch (e) {
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
